import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../v2/ui/components/button';
import CandidateFieldInput from './candidate-field-input';
import ApplicationResult from './application-result';
import {
  buildAnswers,
  enabledFields,
  validateAnswer,
  validateCv,
} from './application-rules';
import { resolveServerError } from './application-errors';
import { useApplyOutcome } from './application-outcome';
import { CV_PICKER, RETRY_COPY, SUBMIT_COPY } from '../data/application-copy';

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Rebecca is called only by the careers-apply function. Anything other than
// that function's JSON result — no response, a platform error page — is
// folded into a failure the form can show.
const apply = async ({ file, positionId, answers }) => {
  try {
    const response = await fetch('/.netlify/functions/careers-apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        positionId,
        answers,
        file: { name: file.name, data: await readAsBase64(file) },
      }),
    });
    const result = await response.json().catch(() => null);
    return response.ok && result && typeof result.ok === 'boolean'
      ? result
      : { ok: false, status: response.status };
  } catch (error) {
    return { ok: false, status: 0 };
  }
};

const kilobytes = (file) => (file.size / 1024).toFixed(0);

// The application form for one position: its CV, its enabled candidate
// fields, and — once Rebecca accepts the upload — the outcome in its place.
const ApplicationForm = ({ position }) => {
  const fields = useMemo(
    () => enabledFields(position.candidate_fields),
    [position.candidate_fields],
  );
  const emailField = fields.find((field) => field.subtype === 'EMAIL');
  const formId = `apply-${position.id.replace(/[^A-Za-z0-9-]/g, '-')}`;

  const [cv, setCv] = useState(null);
  const [answers, setAnswers] = useState(() =>
    fields.reduce((all, field) => ({ ...all, [field.id]: '' }), {}),
  );
  const [errors, setErrors] = useState({});
  // Fields re-validate as they change only after a first submit attempt.
  const [attempted, setAttempted] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const outcome = useApplyOutcome(submission);

  const cvInputRef = useRef(null);
  const fieldRefs = useRef({});
  const focusOnReturn = useRef(null);

  const clearGeneralError = () =>
    setServerError((current) =>
      current && current.kind === 'general' ? null : current,
    );

  const selectCv = (file) => {
    clearGeneralError();
    setCv(file);
    // A CV is checked as soon as it is picked, not only on submit.
    setErrors((current) => ({ ...current, cv: validateCv(file) }));
  };

  const changeAnswer = (field, value) => {
    setAnswers((current) => ({ ...current, [field.id]: value }));
    if (attempted) {
      setErrors((current) => ({
        ...current,
        [field.id]: validateAnswer(field, value),
      }));
    }
    // A field's server error lasts until that field is edited.
    setServerError((current) =>
      current && current.kind === 'field' && current.fieldId === field.id
        ? null
        : current,
    );
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors = { cv: validateCv(cv) };
    fields.forEach((field) => {
      nextErrors[field.id] = validateAnswer(field, answers[field.id]);
    });
    setErrors(nextErrors);
    setAttempted(true);

    const firstInvalid = ['cv']
      .concat(fields.map((field) => field.id))
      .find((key) => nextErrors[key]);
    if (firstInvalid) {
      clearGeneralError();
      const target =
        firstInvalid === 'cv'
          ? cvInputRef.current
          : fieldRefs.current[firstInvalid];
      if (target) target.focus();
      return;
    }

    setServerError(null);
    setSubmitting(true);
    const result = await apply({
      file: cv,
      positionId: position.id,
      answers: buildAnswers(fields, answers),
    });
    setSubmitting(false);

    if (result.ok) {
      setSubmission({ submissionId: result.submissionId });
    } else {
      setServerError(
        resolveServerError(
          result,
          fields.map((field) => field.id),
        ),
      );
    }
  };

  // A server field error moves focus to its input once the inputs are
  // re-enabled; a disabled input can't take focus.
  useEffect(() => {
    if (serverError && serverError.kind === 'field' && !submitting) {
      const target = fieldRefs.current[serverError.fieldId];
      if (target) target.focus();
    }
  }, [serverError, submitting]);

  const showingResult = outcome !== null;

  useEffect(() => {
    if (!showingResult && focusOnReturn.current) {
      focusOnReturn.current.focus();
      focusOnReturn.current = null;
    }
  }, [showingResult]);

  // UNDELIVERABLE_EMAIL sends the candidate back to the form, keeping every
  // answer: with the CV when there is an email field to correct, without it
  // when the address can only come from a corrected CV.
  let retry;
  if (outcome && outcome.stage === 'done') {
    if (outcome.status === 'UNDELIVERABLE_EMAIL' && emailField) {
      retry = {
        label: RETRY_COPY.withEmailField,
        onClick: () => {
          focusOnReturn.current = fieldRefs.current[emailField.id];
          setSubmission(null);
        },
      };
    } else if (outcome.status === 'UNDELIVERABLE_EMAIL') {
      retry = {
        label: RETRY_COPY.withoutEmailField,
        onClick: () => {
          setCv(null);
          focusOnReturn.current = cvInputRef.current;
          setSubmission(null);
        },
      };
    }
  }

  const fieldError = (fieldId) =>
    errors[fieldId] ||
    (serverError &&
    serverError.kind === 'field' &&
    serverError.fieldId === fieldId
      ? serverError.message
      : null);

  const cvInputId = `${formId}-cv`;

  return (
    <div className="__application">
      {outcome && (
        <ApplicationResult
          outcome={outcome}
          emailFieldName={emailField ? emailField.name : undefined}
          retry={retry}
        />
      )}
      {/* Hidden rather than unmounted while a result shows, so a retry
          returns to the answers exactly as they were. */}
      <form noValidate hidden={showingResult} onSubmit={onSubmit}>
        <div className="__application-field">
          <p id={`${cvInputId}-label`} className="__application-label">
            {CV_PICKER.label}
          </p>
          <input
            id={cvInputId}
            ref={cvInputRef}
            className="__application-cv-input"
            type="file"
            accept="application/pdf"
            disabled={submitting}
            aria-labelledby={`${cvInputId}-label`}
            aria-invalid={errors.cv ? true : undefined}
            aria-describedby={errors.cv ? `${cvInputId}-error` : undefined}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              // Cleared so picking the same file again still fires a change.
              e.target.value = '';
              if (file) selectCv(file);
            }}
          />
          <label htmlFor={cvInputId} className="__application-cv-box">
            {CV_PICKER.box}
          </label>
          {cv && (
            <p className="__application-cv-selected">
              Selected: {cv.name} ({kilobytes(cv)} KB)
            </p>
          )}
          {errors.cv && (
            <p
              id={`${cvInputId}-error`}
              className="__application-error"
              role="alert"
            >
              {errors.cv}
            </p>
          )}
        </div>

        {fields.map((field) => (
          <CandidateFieldInput
            key={field.id}
            field={field}
            inputId={`${formId}-${field.id}`}
            inputRef={(node) => {
              fieldRefs.current[field.id] = node;
            }}
            value={answers[field.id]}
            error={fieldError(field.id)}
            disabled={submitting}
            onChange={(value) => changeAnswer(field, value)}
          />
        ))}

        <div className="__application-submit">
          <Button
            type="submit"
            variant="orange"
            loading={submitting}
            disabled={submitting}
          >
            {submitting ? SUBMIT_COPY.busy : SUBMIT_COPY.idle}
          </Button>
          {serverError && serverError.kind === 'general' && (
            <p className="__application-error" role="alert">
              {serverError.message}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
